import { useState } from "react";
import { motion } from "framer-motion";
import {
  Barcode as BarcodeIcon,
  Loader2,
  Search,
  Beef,
  Wheat,
  Flame,
  Droplet,
  Info,
  ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";

// Free public API — no auth needed
const OFF = "https://world.openfoodfacts.org/api/v2/product";

const SAMPLES = [
  { code: "8901491101609", label: "Parle-G" },
  { code: "8901058851557", label: "Maggi Noodles" },
  { code: "5449000000996", label: "Coca-Cola" },
];

const lookup = async (code) => {
  const res = await fetch(`${OFF}/${encodeURIComponent(code)}.json`);
  const data = await res.json();
  if (data.status !== 1 || !data.product) throw new Error("Product not found");
  return data.product;
};

export default function BarcodeTab() {
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [product, setProduct] = useState(null);

  const handleBarcode = async (code) => {
    setBusy(true);
    setProduct(null);
    try {
      const p = await lookup(code);
      setProduct({
        code,
        name: p.product_name || p.generic_name || "Unnamed product",
        brand: p.brands || "",
        image: p.image_front_url || p.image_url || "",
        serving: p.serving_size || "100 g",
        nutriments: p.nutriments || {},
        ingredients: (p.ingredients_text_en || p.ingredients_text || "").trim(),
        nova: p.nova_group,
        nutriscore: p.nutriscore_grade,
      });
    } catch (e) {
      toast.error(
        "Product not in the Open Food Facts database. Try another barcode."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const c = manual.trim();
    if (!c) return;
    handleBarcode(c);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Lookup */}
      <div
        data-testid="barcode-panel"
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Look up a packaged food
        </div>
        <div className="mt-4 relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-white/10 flex items-center justify-center">
          <div className="text-center px-6">
            <BarcodeIcon className="w-14 h-14 text-emerald-300 mx-auto" />
            <div className="mt-4 font-display text-xl font-semibold">
              Enter a barcode
            </div>
            <div className="mt-2 text-sm text-zinc-300 max-w-xs">
              Type the 8–13 digit barcode from the pack. We&apos;ll look up its
              nutrition instantly from Open Food Facts.
            </div>
          </div>
        </div>

        <form onSubmit={submitManual} className="mt-4 flex gap-2">
          <input
            data-testid="barcode-manual"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="e.g., 8901491101609"
            className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-400"
          />
          <button
            data-testid="barcode-manual-submit"
            disabled={!manual.trim() || busy}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-4 py-2.5 text-sm hover:bg-cyan-400 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Look up
          </button>
        </form>

        <div className="mt-5 pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Try these popular Indian barcodes
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {SAMPLES.map((s) => (
              <button
                key={s.code}
                data-testid={`barcode-sample-${s.code}`}
                onClick={() => {
                  setManual(s.code);
                  handleBarcode(s.code);
                }}
                className="text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-zinc-200"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
          <Info className="w-3.5 h-3.5 mt-0.5 text-cyan-300 flex-shrink-0" />
          Camera scanning is coming soon on mobile. For now, type or paste the
          barcode number.
        </div>
      </div>

      {/* Result */}
      <div
        data-testid="barcode-result"
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 min-h-[420px]"
      >
        {busy && !product ? (
          <div className="h-full flex items-center justify-center text-zinc-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Looking up in Open Food Facts…
          </div>
        ) : !product ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-emerald-300" />
            </div>
            <div className="mt-4 font-display text-lg font-semibold">
              Look up any packaged food
            </div>
            <p className="mt-2 text-sm text-zinc-400 max-w-sm">
              Data comes from Open Food Facts — a free, community-run
              database. Coverage varies by region.
            </p>
          </div>
        ) : (
          <ProductCard product={product} />
        )}
      </div>
    </div>
  );
}

const NUTRISCORE_COLORS = {
  a: "#059669",
  b: "#84cc16",
  c: "#facc15",
  d: "#f97316",
  e: "#dc2626",
};

const ProductCard = ({ product }) => {
  const n = product.nutriments || {};
  const per100 = {
    kcal: n["energy-kcal_100g"] ?? n["energy-kcal"],
    protein: n.proteins_100g ?? n.proteins,
    carbs: n.carbohydrates_100g ?? n.carbohydrates,
    fat: n.fat_100g ?? n.fat,
    sugar: n.sugars_100g ?? n.sugars,
    salt: n.salt_100g ?? n.salt,
    fiber: n.fiber_100g ?? n.fiber,
  };
  const nsColor = NUTRISCORE_COLORS[(product.nutriscore || "").toLowerCase()];
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-start gap-4">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-24 h-24 rounded-2xl object-cover border border-white/10"
          />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <ShoppingBag className="w-6 h-6 text-zinc-500" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            {product.brand || "Packaged food"}
          </div>
          <div className="font-display text-2xl font-semibold text-white leading-tight mt-0.5">
            {product.name}
          </div>
          <div className="mt-1 text-xs text-zinc-500">Barcode {product.code}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {product.nutriscore && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full"
                style={{ background: `${nsColor}30`, color: nsColor }}
              >
                Nutri-Score {product.nutriscore.toUpperCase()}
              </span>
            )}
            {product.nova && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">
                NOVA {product.nova}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCell Icon={Flame} label="kcal" value={per100.kcal} accent="#fbbf24" unit="/100g" />
        <StatCell Icon={Beef} label="Protein" value={per100.protein} accent="#f87171" unit="g" />
        <StatCell Icon={Wheat} label="Carbs" value={per100.carbs} accent="#facc15" unit="g" />
        <StatCell Icon={Droplet} label="Fat" value={per100.fat} accent="#a78bfa" unit="g" />
        <StatCell label="Sugar" value={per100.sugar} accent="#fb7185" unit="g" />
        <StatCell label="Fiber" value={per100.fiber} accent="#34d399" unit="g" />
        <StatCell label="Salt" value={per100.salt} accent="#e879f9" unit="g" />
        <StatCell label="Serving" value={product.serving} accent="#67e8f9" unit="" />
      </div>

      {product.ingredients && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Ingredients
          </div>
          <p className="mt-2 text-xs text-zinc-300 leading-relaxed line-clamp-5">
            {product.ingredients}
          </p>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
        <Info className="w-3.5 h-3.5 mt-0.5 text-cyan-300 flex-shrink-0" />
        Values are per 100 g/mL from Open Food Facts. Ultra-processed foods
        (NOVA 4) are best eaten sparingly.
      </div>
    </motion.div>
  );
};

const StatCell = ({ Icon, label, value, accent, unit }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" style={{ color: accent }} />}
      {label}
    </div>
    <div
      className="mt-1 font-display text-lg font-semibold tabular-nums"
      style={{ color: accent }}
    >
      {value != null && value !== "" ? value : "—"}
      {unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}
    </div>
  </div>
);
